import React, { Suspense, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";

import Home from "@/pages/Home";
import Updates from "@/pages/Updates";
import JudgmentDetail from "@/pages/JudgmentDetail";
import SupremeCourtLawyers from "@/pages/SupremeCourtLawyers";
import LawyerProfile from "@/pages/LawyerProfile";
import CityLawyer from "@/pages/CityLawyer";

// Code-split secondary public pages and the whole admin area so the mobile
// first-load bundle stays small (LCP / TBT).
const lazy = (loader) => {
  const C = React.lazy(loader);
  return (props) => (
    <Suspense fallback={<RouteLoader />}>
      <C {...props} />
    </Suspense>
  );
};
const Team = lazy(() => import("@/pages/Team"));
const Advice = lazy(() => import("@/pages/Advice"));
const About = lazy(() => import("@/pages/About"));
const LawyerHighCourt = lazy(() => import("@/pages/LawyerHighCourt"));
const CityLawyerGuide = lazy(() => import("@/pages/CityLawyerGuide"));
const PracticeAreaPage = lazy(() => import("@/pages/PracticeAreaPage"));
const Resources = lazy(() => import("@/pages/Resources"));
const Insights = lazy(() => import("@/pages/Insights"));
const InsightDetail = lazy(() => import("@/pages/InsightDetail"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUpload = lazy(() => import("@/pages/admin/AdminUpload"));
const AdminBatchUpload = lazy(() => import("@/pages/admin/AdminBatchUpload"));
const AdminNewJudgment = lazy(() => import("@/pages/admin/AdminNewJudgment"));
const AdminJudgments = lazy(() => import("@/pages/admin/AdminJudgments"));
const AdminEditJudgment = lazy(() => import("@/pages/admin/AdminEditJudgment"));
const AdminTeam = lazy(() => import("@/pages/admin/AdminTeam"));
const AdminLawyers = lazy(() => import("@/pages/admin/AdminLawyers"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminCallbacks = lazy(() => import("@/pages/admin/AdminCallbacks"));
const AdminSiteContent = lazy(() => import("@/pages/admin/AdminSiteContent"));
const AdminMentorship = lazy(() => import("@/pages/admin/AdminMentorship"));
const AdminJuniorHub = lazy(() => import("@/pages/admin/AdminJuniorHub"));
const AdminArticles = lazy(() => import("@/pages/admin/AdminArticles"));
const AdminInfographicPrompt = lazy(() => import("@/pages/admin/AdminInfographicPrompt"));
const AdminContactTracking = lazy(() => import("@/pages/admin/AdminContactTracking"));

function RouteLoader() {
  // Fixed-height placeholder so lazy chunks never shift the layout
  return <div className="min-h-[60vh]" aria-busy="true" />;
}

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function AuthCallback() {
  const processed = useRef(false);
  const navigate = useNavigate();
  const { setSessionToken } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const m = (window.location.hash || "").match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/admin/login");
      return;
    }
    const sid = decodeURIComponent(m[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        window.history.replaceState(null, "", window.location.pathname);
        if (data.is_admin) {
          await setSessionToken(data.session_token);
          navigate("/admin");
        } else {
          setError("This Google account is not authorised for admin access.");
          setTimeout(() => navigate("/admin/login"), 3000);
        }
      } catch (e) {
        setError("Google sign-in failed. Please try again.");
        setTimeout(() => navigate("/admin/login"), 3000);
      }
    })();
  }, [navigate, setSessionToken]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <ShieldAlert className="h-10 w-10 text-destructive" />
        <p className="max-w-sm text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }
  return <FullLoader />;
}

function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

/** Preserves the :citySlug param while redirecting legacy city-guide URLs
 * ("/lawyer-in-x" and "/advocate-in-x") to the canonical
 * /legal-help/lawyer-in-x route. Falls back to home for any other
 * unmatched single-segment path (same behaviour as the "*" catch-all). */
function LegacyCityOrNotFound() {
  const { legacyCityParam } = useParams();
  const m = /^(?:lawyer-in|advocate-in)-(.+)$/i.exec(legacyCityParam || "");
  if (m) return <Navigate to={`/legal-help/lawyer-in-${m[1]}`} replace />;
  return <Navigate to="/" replace />;
}

const P = (el) => <PublicLayout>{el}</PublicLayout>;
const A = (el) => (
  <ProtectedRoute>
    <AdminLayout>{el}</AdminLayout>
  </ProtectedRoute>
);

function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={P(<Home />)} />
      <Route path="/updates" element={P(<Updates />)} />
      <Route path="/judgments/:slug" element={P(<JudgmentDetail />)} />
      <Route path="/team" element={P(<Team />)} />
      <Route path="/advice" element={P(<Advice />)} />
      <Route path="/free-legal-help" element={P(<Advice />)} />
      <Route path="/about" element={P(<About />)} />
      <Route path="/supreme-court-lawyers" element={P(<SupremeCourtLawyers />)} />
      <Route path="/supreme-court-lawyers/:id" element={P(<LawyerProfile />)} />
      <Route path="/lawyer-supreme-court" element={<Navigate to="/supreme-court-lawyers" replace />} />
      <Route path="/lawyer-high-court" element={P(<LawyerHighCourt />)} />
      <Route path="/supreme-court-lawyer-for/:practice" element={P(<PracticeAreaPage />)} />
      <Route path="/supreme-court-lawyer/:city" element={P(<CityLawyer />)} />
      <Route path="/legal-help/:cityParam" element={P(<CityLawyerGuide />)} />
      <Route path="/:legacyCityParam" element={<LegacyCityOrNotFound />} />
      <Route path="/junior-lawyer-hub" element={<Navigate to="/resources" replace />} />
      <Route path="/resources" element={P(<Resources />)} />
      <Route path="/resources/:category" element={P(<Resources />)} />
      <Route path="/resources/:category/:sub" element={P(<Resources />)} />
      <Route path="/insights" element={P(<Insights />)} />
      <Route path="/insights/:slug" element={P(<InsightDetail />)} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={A(<AdminDashboard />)} />
      <Route path="/admin/upload" element={A(<AdminUpload />)} />
      <Route path="/admin/batch" element={A(<AdminBatchUpload />)} />
      <Route path="/admin/new" element={A(<AdminNewJudgment />)} />
      <Route path="/admin/judgments" element={A(<AdminJudgments />)} />
      <Route path="/admin/judgments/:id" element={A(<AdminEditJudgment />)} />
      <Route path="/admin/judgments/:id/edit" element={A(<AdminEditJudgment />)} />
      <Route path="/admin/team" element={A(<AdminTeam />)} />
      <Route path="/admin/lawyers" element={A(<AdminLawyers />)} />
      <Route path="/admin/bookings" element={A(<AdminBookings />)} />
      <Route path="/admin/callbacks" element={A(<AdminCallbacks />)} />
      <Route path="/admin/content" element={A(<AdminSiteContent />)} />
      <Route path="/admin/mentorship" element={A(<AdminMentorship />)} />
      <Route path="/admin/junior-hub" element={A(<AdminJuniorHub />)} />
      <Route path="/admin/articles" element={A(<AdminArticles />)} />
      <Route path="/admin/infographic-prompt" element={A(<AdminInfographicPrompt />)} />
      <Route path="/admin/contact-tracking" element={A(<AdminContactTracking />)} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
